/**
 * Pure helper functions extracted from UniversalNode component.
 * These functions are free of React and have no side-effects.
 */

import type { NodeInput } from '@/features/backtest/types/nodes'
import type { ParameterValue, ParametersRecord } from '@/features/backtest/schemas/parameter-value'
import type {
  UniversalNodeSchema,
  InputDefinition,
  OutputDefinition,
  ParameterDefinition,
} from '@/features/backtest/schemas/universal-node-schema'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'

// --- Static records ---

/** Maps source node types to their output value types for Switcher nodes. */
export const NODE_TYPE_TO_OUTPUT_TYPE: Record<string, string> = {
  condition: 'boolean',
  crossover: 'boolean',
  merge: 'boolean',
  barsConditionCounter: 'boolean',
  cooldown: 'boolean',
  timeout: 'boolean',
  priceData: 'number',
  volumeData: 'number',
  marketData: 'ohlcv',
}

/** Node types whose outputs are boolean signals (no field selection needed). */
export const BOOLEAN_OUTPUT_NODES = [
  'condition',
  'merge',
  'crossover',
  'timeout',
  'cooldown',
  'barsConditionCounter',
  'timeSignal',
]

/** Node types that provide OHLCV data (used for field selection logic). */
export const OHLCV_SOURCES = ['priceData', 'volumeData', 'marketData']

// --- Helper 1: getDynamicOutputType ---

/**
 * Determines the dynamic output type for a Switcher node's output definition.
 * For non-dynamic or non-switcher outputs, returns the output's declared type.
 */
export function getDynamicOutputType(
  outputDef: OutputDefinition,
  schemaType: string,
  connectedSources: Record<string, string | null | string[]>,
): string {
  if ((outputDef.type as string) !== 'dynamic' || schemaType !== 'switcher') {
    return outputDef.type
  }

  // For Switcher nodes, determine type from first connected input using _connections
  // _connections already stores source node TYPES (not IDs)
  // Filter to only string values (exclude 'multiple' array entries from Sum/Average nodes)
  const stringConnectionValues = Object.values(connectedSources).filter(
    (val): val is string => typeof val === 'string' && val !== null,
  )
  const firstConnectedType = stringConnectionValues[0]

  if (!firstConnectedType) {
    return 'number' // Default when no connections
  }

  return NODE_TYPE_TO_OUTPUT_TYPE[firstConnectedType] || 'number'
}

// --- Helper 2: getInputFieldOptions ---

/**
 * Returns the list of field-selection options for an input dropdown.
 * Returns an empty array when no options should be shown.
 */
export function getInputFieldOptions(
  inputDef: InputDefinition,
  isConnected: boolean,
  connectedSourceType: string | null,
): Array<{ value: string; label: string }> {
  if (!isConnected || !connectedSourceType) return []

  // connectedSourceType is the source node TYPE from _connections (not an ID)

  // Skip field selection for boolean output nodes - their outputs are signals, not fields
  // When connected to condition, merge, crossover, etc., the user already selected which output handle to connect
  if (BOOLEAN_OUTPUT_NODES.includes(connectedSourceType)) {
    return []
  }

  // If fieldSelection is dynamic, get options from connected node's outputs
  if (inputDef.fieldSelection?.dynamic) {
    // For OHLCV data sources, use fallbackOptions first
    if (OHLCV_SOURCES.includes(connectedSourceType) && inputDef.fieldSelection.fallbackOptions) {
      return inputDef.fieldSelection.fallbackOptions
    }

    // For technical indicators with multiple outputs (like Bollinger Bands), get from schema
    const connectedSchema = UniversalNodeRegistry.getSchema(connectedSourceType)
    if (connectedSchema && connectedSchema.outputs && connectedSchema.outputs.length > 0) {
      // Return output options from the connected node's schema
      return connectedSchema.outputs.map((output) => ({
        value: output.id,
        label: output.displayName || output.id,
      }))
    }

    return []
  }

  // Legacy: Check if connected node has multiple outputs by looking up its schema
  const connectedSchema = UniversalNodeRegistry.getSchema(connectedSourceType)
  if (connectedSchema && connectedSchema.outputs && connectedSchema.outputs.length > 1) {
    // Return output options from the connected node's schema
    return connectedSchema.outputs.map((output) => ({
      value: output.id,
      label: output.displayName || output.id,
    }))
  }

  // Fall back to OHLCV field selection for data sources that support it
  if (inputDef.fieldSelection) {
    if (OHLCV_SOURCES.includes(connectedSourceType)) {
      return (
        inputDef.fieldSelection.options || [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ]
      )
    }
  }

  return []
}

// --- Helper 3: getFieldSelectionValue ---

/**
 * Resolves the current field-selection value for an input, accounting for
 * connection state and available options.
 */
export function getFieldSelectionValue(
  inputDef: InputDefinition,
  currentInput: NodeInput | undefined,
  connectedSourceType: string | null,
): string | null {
  if (!connectedSourceType) return currentInput?.source || null

  const fieldOptions = getInputFieldOptions(inputDef, true, connectedSourceType)
  if (fieldOptions.length === 0) return 'close'

  // Materialized recipe inputs keep the upstream step id in `source` and the
  // canonical OHLCV selection in `field`. Live-editor inputs historically kept
  // the selection in `source`, so prefer a valid field and retain that fallback.
  const currentSelection = [currentInput?.field, currentInput?.source].find(
    (value): value is string =>
      typeof value === 'string' && fieldOptions.some((option) => option.value === value),
  )
  if (currentSelection) return currentSelection

  // For multi-output indicators (like Bollinger Bands), default to first output
  if (inputDef.fieldSelection?.dynamic) {
    // connectedSourceType is already the node type from _connections
    if (OHLCV_SOURCES.includes(connectedSourceType) && inputDef.fieldSelection.defaultField) {
      // For OHLCV sources, respect the defaultField setting
      return inputDef.fieldSelection.defaultField
    }

    // For technical indicators, use first output
    return fieldOptions[0]?.value || inputDef.fieldSelection.defaultField
  }

  // For OHLCV connections, default to 'close' since it's most commonly used for technical indicators
  if (fieldOptions.some((opt) => opt.value === 'close')) {
    return 'close'
  }

  // Otherwise, fall back to first option
  return fieldOptions[0]?.value || 'close'
}

// --- Helper 4: getParameterValue / setParameterValue ---

/** One element of the descriptor-array parameter form. */
type ParamDescriptor = { name?: string; value?: ParameterValue }

/**
 * Reads a parameter value, tolerating BOTH the descriptor-array form and the
 * record form. The array form is what most saved strategies actually carry; a
 * plain `parameters[name]` access returns undefined on an array and silently
 * falls back to the schema default — which is why nodes used to render defaults
 * instead of their saved values. Accepts `unknown` because call sites hold the
 * value at varying static types (`ParametersRecord`, node `data`, etc.); the
 * shape is resolved at runtime.
 */
export function getParameterValue(
  parameters: unknown,
  paramName: string,
  fallback?: ParameterValue,
): ParameterValue | undefined {
  if (Array.isArray(parameters)) {
    const hit = (parameters as ParamDescriptor[]).find((p) => p?.name === paramName)
    return (hit?.value as ParameterValue | undefined) ?? fallback
  }
  if (parameters && typeof parameters === 'object') {
    return ((parameters as ParametersRecord)[paramName] as ParameterValue | undefined) ?? fallback
  }
  return fallback
}

/**
 * Writes a parameter value while PRESERVING the input's shape. For the array
 * form it updates the matching element's `value` (appending a new entry if the
 * name is absent); for the record form it sets the key. Spreading an array into
 * an object literal (`{ ...arrayParams, [name]: value }`) corrupts it into
 * `{ '0': …, '1': …, name: value }`, which the backend rejects with
 * "parameters: Input should be a valid list" — this helper avoids that.
 */
export function setParameterValue(
  parameters: unknown,
  paramName: string,
  value: ParameterValue,
): ParametersRecord | ParamDescriptor[] {
  if (Array.isArray(parameters)) {
    let found = false
    const next = (parameters as ParamDescriptor[]).map((p) => {
      if (p?.name === paramName) {
        found = true
        return { ...p, value }
      }
      return p
    })
    if (!found) next.push({ name: paramName, value })
    return next
  }
  return { ...((parameters as ParametersRecord) || {}), [paramName]: value }
}

// --- Helper 5: getCalculatedPercentage ---

/**
 * Auto-computes the remaining percentage for a Multi Target node, subtracting
 * all previously-set percentages from 100. 0 for every other node type.
 */
export function getCalculatedPercentage(
  schemaType: string,
  connectedSources: Record<string, string | null | string[]>,
  parameters: ParametersRecord,
): number {
  const connectedInputCount = Object.keys(connectedSources).filter(
    (key) => connectedSources[key],
  ).length

  if (schemaType === 'multiTarget') {
    let total = 0
    for (let i = 1; i < connectedInputCount; i++) {
      total += Number(getParameterValue(parameters, `target${i}Percent`, 34)) || 0
    }
    return 100 - total
  }
  return 0
}

// --- Helper 6: getDynamicInputs ---

/**
 * Every socket a node has right now: its declared inputs, or for a node with
 * no declared inputs (Merge, Sum) the filled ones plus one empty. Declared,
 * numbered inputs (`inputHandler.reveal: 'progressive'`) grow the same way.
 * Which of these the face draws is a separate rule (schemas/input-activity.ts),
 * so an inactive socket stays in this list: the dev graph API resolves input
 * names against it.
 */
export function getDynamicInputs(
  schema: UniversalNodeSchema,
  connectedSources: Record<string, string | null | string[]>,
  // Kept for callers; which sockets are in play is decided by input-activity.
  _parameters?: ParametersRecord,
): InputDefinition[] {
  // Check if this node has multi-input handler
  if (schema.inputHandler?.type !== 'multi') {
    return schema.inputs
  }

  // For multi-input nodes, determine how many inputs to show based on connections
  const connectedHandles = Object.keys(connectedSources).filter((key) => connectedSources[key])
  const connectedCount = connectedHandles.length

  const minInputs = schema.inputHandler.minInputs || 2
  const maxInputs = schema.inputHandler.maxInputs || 50

  // Show connected inputs + 1 extra, respecting min/max bounds
  const inputCount = Math.min(maxInputs, Math.max(minInputs, connectedCount + 1))

  // Declared inputs: numbered ones grow one at a time, named ones all show.
  if (schema.inputs.length > 0) {
    return schema.inputHandler.reveal === 'progressive'
      ? schema.inputs.slice(0, inputCount)
      : schema.inputs
  }

  // For dynamic input nodes (like merge, sum, average), generate input_1, input_2, etc.
  const dynamicInputs: InputDefinition[] = []
  for (let i = 0; i < inputCount; i++) {
    dynamicInputs.push({
      id: `input_${i + 1}`,
      displayName:
        schema.category === 'math' || schema.category === 'logic' ? '' : `Input ${i + 1}`, // Empty for math/logic nodes to show A, B, C letters
      acceptedTypes: schema.category === 'logic' ? ['boolean'] : ['number', 'ohlcv'],
      acceptedSources:
        schema.category === 'logic'
          ? ['logic', 'strategy', 'filters']
          : ['priceData', 'indicators', 'math', 'utilities'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    })
  }

  return dynamicInputs
}

// --- Which parameters a node shows right now ---

/**
 * Whether a parameter draws a field right now: not stored-only (`hidden`), not
 * stood in for by a wire (`hiddenWhenConnected`), and every setting it
 * `dependsOn` currently holds one of the listed values. The parameters
 * section and a sectioned face's paired rows both ask this, so a field never
 * shows in one and hides in the other.
 */
export function isParameterShown(
  def: Pick<ParameterDefinition, 'hidden' | 'hiddenWhenConnected' | 'dependsOn' | 'dependsOnAll'>,
  getValue: (name: string) => unknown,
  connectedSources: Record<string, unknown>,
): boolean {
  if (def.hidden === true) return false
  if (def.hiddenWhenConnected !== undefined && Boolean(connectedSources[def.hiddenWhenConnected]))
    return false
  const dependencies = [...(def.dependsOn ? [def.dependsOn] : []), ...(def.dependsOnAll ?? [])]
  return dependencies.every((dependency) => {
    const value = getValue(dependency.parameter)
    return value !== undefined && dependency.values.includes(value as string | number | boolean)
  })
}

// --- Static or wired: one rewrite of an input's entry ---

/**
 * The node's inputs after switching one discriminated-union input (a
 * Condition's B) between a wire and a typed value. Switching to Static brings
 * back the number typed before (kept as `_staticValue` while wired), else the
 * schema default; switching to Node keeps the typed number for later. Other
 * inputs are returned untouched. The one rewrite both the type picker and a
 * parameter change use (audit 2026-10-06: two stale rewrites ran per click).
 */
export function inputsForTypeSwitch(
  inputs: NodeInput[] | undefined,
  inputDef: InputDefinition,
  newType: 'node' | 'static',
): NodeInput[] {
  const union = inputDef.discriminatedUnion
  if (!union) return inputs ?? []
  const current = (inputs ?? []).find((input) => input?.name === inputDef.id)
  const next: NodeInput =
    newType === 'static'
      ? {
          name: inputDef.id,
          inputType: 'static',
          value:
            current?.inputType === 'static'
              ? current.value
              : ((current?._staticValue as string | number | boolean | undefined) ??
                union.defaultStaticValue ??
                0),
        }
      : {
          name: inputDef.id,
          inputType: 'node',
          source: null,
          ...(current?.inputType === 'static' && current.value !== undefined
            ? { _staticValue: current.value }
            : {}),
        }
  return current
    ? (inputs ?? []).map((input) => (input?.name === inputDef.id ? next : input))
    : [...(inputs ?? []), next]
}
